import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  Output,
  QueryList,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Subject } from 'rxjs';

import { GasInputBoolean } from '@cikrf/gas-utils/decorators';
import { GasNotationPipe } from '@cikrf/gas-utils/pipes';

import { GasIcon } from '../../utils/gas-icons/icon/gas-icon.namespace';
import { GasFiltersDirective } from '../gas-filters/gas-filters.directive';
import { GasFormKeyDirective } from '../../utils/gas-form-key/gas-form-key.directive';
import { GasColor } from '../../core/colors/color.namespace';
import { GasTable } from '../gas-table/gas-table.namespace';
import { GasForm } from '../../forms/gas-form.namespace';
import { GasObjectHelper } from '@cikrf/gas-utils/helpers';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import FilterValue = GasTable.FilterValue;
import FilterButtonCustomFunctions = GasTable.FilterButtonCustomFunctions;

/** @see https://www.figma.com/file/yqBeq1MiGfpdN62SvmA8tg/RTLABS_GAS_DS_1?node-id=1792%3A28830 */
/** @description Конструируемая шапка таблицы, включает в себя блок фильтрации */
@UntilDestroy()
@Component({
  selector: 'gas-table-header',
  templateUrl: './gas-table-header.component.html',
  styleUrls: ['./gas-table-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    GasNotationPipe,
  ],
})
export class GasTableHeaderComponent implements AfterViewInit, OnInit {
  @Input()
  public title: string | TemplateRef<ElementRef>;

  @Input()
  public totalText: string;

  @GasInputBoolean()
  @Input()
  public isSearchFilter: string | boolean = false;

  @GasInputBoolean()
  @Input()
  public showFiltersCounter: string | boolean = true;

  @Input('clearAllFilter')
  public clearAllFilter$: Subject<void>;

  @Input('clearCurrentFilter')
  public clearCurrentFilter$: Subject<GasFormKeyDirective | GasFormKeyDirective[]>;

  @HostBinding('class._vertical-view')
  @GasInputBoolean()
  @Input()
  public verticalView: string | boolean = false;

  @Input()
  public actions: GasTable.Action[] = [];

  @Input()
  public customFilterButtonsTemplate: TemplateRef<HTMLElement>;

  @Input()
  public hideFilterAfterApplying: boolean;

  @Input()
  public hideHeaderBar = false;

  @Input()
  public filterButtonCustomFunctions: FilterButtonCustomFunctions;

  @Input()
  public set textPayload(texts: GasTable.Texts) {
    this.texts = GasObjectHelper.mergeDeep(GasTable.DEFAULT_TEXTS, texts);
  }

  @Input()
  public filterAlwaysVisible = false;

  // eslint-disable-next-line @typescript-eslint/adjacent-overload-signatures
  public get textPayload(): GasTable.Texts {
    return this.texts;
  }

  @Output('filterChanged')
  public filterChanged$ = new EventEmitter<FilterValue>();

  @ViewChild(GasFiltersDirective)
  public gasFiltersDirective: GasFiltersDirective;

  @ViewChild('filtersTemplateContent')
  public filtersTemplateContent: ElementRef;

  @ContentChildren(GasFormKeyDirective)
  public gasInputs: QueryList<GasFormKeyDirective>;

  public texts: GasTable.Texts = GasTable.DEFAULT_TEXTS;
  public readonly actionsSize = 16;

  public searchText: string;

  public get isShowActions(): boolean {
    return this.actions.length > 0;
  }

  public get isAdvancedFilterVisible(): boolean {
    return this.isAdvancedFilterAvailable && this.hiddenIsAdvancedFilterVisible;
  }

  public get isAdvancedFilterAvailable(): boolean {
    return this.gasFiltersDirective
      && this.filtersTemplateContent?.nativeElement?.childElementCount;
  }

  public get filterListIconColor(): string {
    return this.isAdvancedFilterApplied ? GasColor.Color.White : GasColor.Color.Brand;
  }

  public get arrowsChevronIconColor(): string {
    return this.isAdvancedFilterApplied ? GasColor.Color.White : GasColor.Color.Brand;
  }

  public get arrowsChevronIconName(): string {
    return this.isAdvancedFilterVisible ? GasIcon.Arrows.ChevronUp : GasIcon.Arrows.ChevronDown;
  }

  public get isAdvancedFilterApplied(): boolean {
    return this.isAdvancedFilterAvailable && this.gasFiltersDirective.state === 'success';
  }

  public get filteredText(): string {
    const countSuccessFilters = this.gasFiltersDirective.modifiedInputs.filter(
      (gasFormKey: GasFormKeyDirective) => gasFormKey.component.state === GasForm.InputState.Success,
    ).length;

    const notation = this.gasNotationPipe.transform(countSuccessFilters, ['параметру', 'параметрам', 'параметрам']);
    return countSuccessFilters
      ? `Отфильтровано по ${countSuccessFilters} ${notation}`
      : null;
  }

  public get isSimpleTitle(): boolean {
    return typeof this.title === 'string';
  }

  private advancedFilterKeys: string[] = [];

  private hiddenIsAdvancedFilterVisible = false;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private gasNotationPipe: GasNotationPipe,
  ) {
  }

  public ngOnInit(): void {
    this.clearAllFilter$
      .pipe(
        untilDestroyed(this),
      )
      .subscribe(() => this.gasFiltersDirective.gasFiltersComponent.clearedAllFilters$.emit());

    if (this.clearCurrentFilter$) {
      this.clearCurrentFilter$
        .pipe(
          untilDestroyed(this),
        )
        .subscribe((directive: GasFormKeyDirective) =>
          this.gasFiltersDirective.gasFiltersComponent.clearCurrentFilters$.emit(directive),
        );
    }
  }

  public ngAfterViewInit(): void {
    this.initFilters();
  }

  public toggleAdvancedFilterVisibility(): void {
    this.hiddenIsAdvancedFilterVisible = !this.hiddenIsAdvancedFilterVisible;
  }

  /** initFilters - метод инициализации связи полей и обёртки расширенного фильтра,
   * также используется в компоненте GasTable */
  public initFilters(gasInputs: QueryList<GasFormKeyDirective> = undefined): void {
    // Если gasInputs не переданы, просто работаем с this.gasInputs
    if (gasInputs) {
      // Если переданы, перезаписываем QueryList имеющимися и кастомными компонентами
      this.gasInputs.reset([...this.gasInputs.toArray(), ...gasInputs]);
    }
    this.gasInputs.forEach(gasInput => {
      this.advancedFilterKeys.push(gasInput.gasFormKey);
      this.initGasFormKeyInput(gasInput);
    });

    // подписка используется для отслеживания добавления/удаления инпутов через директиву ngIf
    this.gasInputs.changes
      .pipe(
        untilDestroyed(this),
      )
      .subscribe((inputs: QueryList<GasFormKeyDirective>) => {
        this.changeDetectorRef.detectChanges();
        const actualAdvancedFilterKeys: string[] = [];

        inputs.forEach(gasInput => {
          actualAdvancedFilterKeys.push(gasInput.gasFormKey);
          if (!this.advancedFilterKeys.includes(gasInput.gasFormKey)) {
            this.initGasFormKeyInput(gasInput);
          }
        });

        this.advancedFilterKeys.forEach(key => {
          if (!actualAdvancedFilterKeys.includes(key)) {
            this.gasFiltersDirective.deleteControl(key);
          }
        });

        this.advancedFilterKeys = actualAdvancedFilterKeys;
      });
  }

  public emitFilter(filter: FilterValue): void {
    this.filterChanged$.emit(filter);
    if (this.hideFilterAfterApplying) {
      this.hiddenIsAdvancedFilterVisible = false;
    }
  }

  private initGasFormKeyInput(gasInput: GasFormKeyDirective): void {
    gasInput.gasFiltersDirective = this.gasFiltersDirective;
    gasInput.init();
  }

}
